import React, { useEffect } from "react";
import { filter } from "rxjs/operators";
import { useObservableValue } from "@iventis/utilities";
import { combineLatest, OperatorFunction } from "rxjs";
import * as d3 from "d3";
import { CalendarSession } from "./calendar-session";
import { Session } from "./types";
import { useGroupAxisScrollOffset, usePropsStream } from "./hooks";
import { ContextPropsData } from "./calendar-context";

interface CalendarPlotProps {
    data: Session[];
    onSessionClick?: (session: Session, event: PointerEvent) => void;
    onSessionContextClick?: (session: Session, event: PointerEvent) => void;
}

export const CalendarPlot: React.FC<CalendarPlotProps> = React.memo(({ data, onSessionClick, onSessionContextClick }) => {
    const props$ = usePropsStream();
    const props = useObservableValue(props$.pipe(filter(Boolean) as OperatorFunction<ContextPropsData | undefined, ContextPropsData>));
    const scrollOffset$ = useGroupAxisScrollOffset();

    // Translate the plotted events along the x axis based on scrollOffset
    // Only the axis is translated here currently because we don't have an observable
    // for Y axis offset, and the offset becomes more tricky since we're using transform.rescaleY
    // Could be addressed in future
    useEffect(() => {
        const sub = combineLatest([scrollOffset$]).subscribe(([scrollOffset]) => {
            d3.select(".CalendarPlot").attr("transform", `translate(${-scrollOffset},0)`);
        });
        return () => sub.unsubscribe();
    }, [scrollOffset$]);

    return props ? (
        <g className="CalendarPlot">
            {data.map((session) => (
                <CalendarSession
                    key={session.id}
                    sessionComponent={props.sessionComponent}
                    sessionTabFormatter={props.sessionTabFormatter}
                    eventUnitComponent={props.eventUnitComponent}
                    session={session}
                    onClick={onSessionClick}
                    onContextClick={onSessionContextClick}
                />
            ))}
        </g>
    ) : (
        <></>
    );
});
